// ─────────────────────────────────────────────────────────────────────────────
// src/features/mdx/components/SavedQueriesDropdown.tsx
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  getSavedQueries,
  deleteSavedQuery,
  updateSavedQuery,
  SavedQuery,
} from "../api";
import { colors } from "../../../shared/ui/tokens";

interface Props {
  onSelect?: (q: SavedQuery) => void;
  refreshToken?: number;
  currentType?: string;
}

// ── AVDASH palette ────────────────────────────────────────────────────────────
const NW = {
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.brand[100],
  violetSoft: colors.neutral[100],
  violetLight: colors.brand[100],
  rose: colors.accent[300],
  white: colors.neutral[0],
  gray50: colors.neutral[50],
  gray100: colors.neutral[100],
  gray200: colors.neutral[200],
  gray300: colors.neutral[300],
  gray500: colors.neutral[500],
  gray700: colors.neutral[700],
  gray800: colors.neutral[800],
  danger: colors.danger[500],
  dangerLight: colors.danger[50],
  success: colors.accent[500],
  successLight: colors.accent[50],
  teal: colors.accent[300],
  blue: colors.brand[600],
};

const FONTS = {
  heading: "Poppins, Inter, system-ui, sans-serif",
  body: "Inter, system-ui, -apple-system, sans-serif",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const TYPE_META: Record<
  string,
  { label: string; icon: string; bg: string; color: string }
> = {
  article: {
    label: "Groupes Article",
    icon: "📦",
    bg: "#184B8C14",
    color: "#184B8C",
  },
  sousfamille: {
    label: "Sous Famille",
    icon: "🏷️",
    bg: "#1D5BA814",
    color: "#1D5BA8",
  },
  saison: { label: "Saison", icon: "⟐", bg: "#00A08C14", color: "#00A08C" },
  groupe: { label: "Groupe", icon: "⊞", bg: "#2077BC14", color: "#2077BC" },
  univers: { label: "Univers", icon: "◍", bg: "#00806F14", color: "#00806F" },
  mdx: { label: "MDX Builder", icon: "⬡", bg: "#4A86C714", color: "#4A86C7" },
  couleur: { label: "Couleur", icon: "🎨", bg: "#1D5BA814", color: "#1D5BA8" },
  "couleur-taille": {
    label: "Couleur-Taille",
    icon: "🎨",
    bg: "#1D5BA814",
    color: "#1D5BA8",
  },
  magazin: { label: "Magazine", icon: "🛍️", bg: "#7DA8D914", color: "#7DA8D9" },
  famille: { label: "Famille", icon: "👪", bg: "#1D5BA814", color: "#1D5BA8" },
  Marque: { label: "Marque", icon: "🏷️", bg: "#7DA8D914", color: "#7DA8D9" },
  taille: { label: "Taille", icon: "📏", bg: "#D98E0414", color: "#D98E04" },
  vendeur: { label: "Vendeur", icon: "🧑‍💼", bg: "#00A08C14", color: "#00A08C" },
};

const SECTION_META: Record<
  string,
  { label: string; icon: string; bg: string; color: string }
> = {
  tableau: { label: "Tableau", icon: "📋", bg: "#33415514", color: "#334155" },
  table: { label: "Tableau", icon: "📋", bg: "#33415514", color: "#334155" },
  dashboard: {
    label: "Dashboard",
    icon: "📊",
    bg: "#184B8C14",
    color: "#184B8C",
  },
  comparaison: {
    label: "Comparaison",
    icon: "⚖️",
    bg: "#1D5BA814",
    color: "#1D5BA8",
  },
  comparison: {
    label: "Comparaison",
    icon: "⚖️",
    bg: "#1D5BA814",
    color: "#1D5BA8",
  },
  evolution: {
    label: "Évolution",
    icon: "📈",
    bg: "#00A08C14",
    color: "#00A08C",
  },
  chart: { label: "Graphique", icon: "📊", bg: "#D98E0414", color: "#D98E04" },
};

function getDimLabel(d: any): string {
  return d?.caption ?? d?.hierarchyName ?? d?.dimensionName ?? "—";
}

interface ParsedComparisonPeriod {
  label?: string;
  dateFrom: string;
  dateTo: string;
  soldeCodePeriode?: string | null;
  demarquePhase?: string | null;
}
function parseComparisonPeriods(raw: any): ParsedComparisonPeriod[] | null {
  if (!raw) return null;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

// ── Sous-composants UI ────────────────────────────────────────────────────────
const Section: React.FC<{
  title: string;
  color: string;
  bg: string;
  border: string;
  children: React.ReactNode;
}> = ({ title, color, bg, border, children }) => (
  <div
    style={{
      background: bg,
      border: `1.5px solid ${border}`,
      borderRadius: 10,
      padding: "10px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
    }}
  >
    <div
      style={{
        fontFamily: "Poppins, Inter, system-ui, sans-serif",
        fontWeight: 700,
        fontSize: 10,
        color,
        textTransform: "uppercase",
        letterSpacing: 1.2,
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const DimRow: React.FC<{ label: string; color: string; items: string[] }> = ({
  label,
  color,
  items,
}) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color,
        background: `${color}14`,
        border: `1px solid ${color}30`,
        borderRadius: 4,
        padding: "2px 6px",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {label}
    </span>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {items.map((item, i) => (
        <span
          key={i}
          style={{
            fontSize: 11,
            color: "#334155",
            background: "#33415514",
            border: "1px solid #33415530",
            borderRadius: 4,
            padding: "2px 7px",
            fontWeight: 600,
          }}
        >
          {item}
        </span>
      ))}
    </div>
  </div>
);

// Styles partagés
const btnSecondary: React.CSSProperties = {
  padding: "8px 18px",
  borderRadius: 9,
  border: `1.5px solid ${NW.gray200}`,
  background: NW.white,
  color: NW.gray500,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: NW.gray500,
  display: "flex",
  alignItems: "center",
  gap: 5,
};
const btnPrimaryBase: React.CSSProperties = {
  padding: "8px 20px",
  borderRadius: 9,
  border: "none",
  background: `linear-gradient(135deg,${NW.violetDark} 0%,${NW.violet} 55%,#1D5BA8 100%)`,
  color: "#FFFFFF",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "Poppins, Inter, system-ui, sans-serif",
  boxShadow: "0 4px 14px rgba(24,75,140,0.30)",
  display: "flex",
  alignItems: "center",
  gap: 5,
};
const iconBtnBase: React.CSSProperties = {
  background: "none",
  border: `1px solid ${NW.gray200}`,
  borderRadius: 6,
  width: 26,
  height: 26,
  cursor: "pointer",
  color: NW.gray500,
  fontSize: 13,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.12s",
  flexShrink: 0,
};

// ─────────────────────────────────────────────────────────────────────────────
// ── Modal Détail + Édition ────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
type ModalMode = "view" | "edit";

interface DetailModalProps {
  query: SavedQuery;
  onClose: () => void;
  onUpdated: (updated: SavedQuery) => void;
}

const DetailModal: React.FC<DetailModalProps> = ({
  query,
  onClose,
  onUpdated,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<ModalMode>("view");
  const [editName, setEditName] = useState((query as any).name ?? "");
  const [editRemarque, setEditRemarque] = useState(
    (query as any).remarque ?? "",
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Escape → fermer
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  // Clic extérieur → fermer
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node))
        onClose();
    };
    document.addEventListener("mousedown", h, true);
    return () => document.removeEventListener("mousedown", h, true);
  }, [onClose]);

  // ── Données enrichies ─────────────────────────────────────────────────────
  const q = query as any;
  const typeMeta = q.type ? TYPE_META[q.type] : undefined;
  const sectionMeta = q.section ? SECTION_META[q.section] : undefined;
  const rowDims: any[] = q.rowsDimensions ?? [];
  const colDims: any[] = q.columnsDimensions ?? [];
  const soldeCode: string | null = q.soldeCodePeriode ?? null;
  const demarquePhase: string | null = q.demarquePhase ?? null;
  const compPeriods = parseComparisonPeriods(q.comparisonPeriods);
  const isComparison =
    q.section === "comparaison" || q.section === "comparison";
  const measures: string[] = q.measures ?? [];

  // ── Sauvegarde ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!editName.trim()) {
      setSaveError("Le nom est requis.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const { data } = await updateSavedQuery(query.id, {
        name: editName.trim(),
        remarque: editRemarque.trim() || null,
      } as any);
      setSaveSuccess(true);
      const updated = {
        ...query,
        ...(data ?? {}),
        name: editName.trim(),
        remarque: editRemarque.trim() || null,
      } as any;
      onUpdated(updated); // mise à jour immédiate dans la liste
      setTimeout(() => {
        setSaveSuccess(false);
        setMode("view"); // revenir en vue (dropdown reste ouvert)
      }, 1200);
    } catch (e: any) {
      setSaveError(e.message ?? "Erreur lors de la modification.");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(13,42,80,0.45)",
          backdropFilter: "blur(3px)",
          zIndex: 9998,
          animation: "nwFadeIn 0.18s ease",
        }}
      />

      {/* Carte */}
      <div
        ref={modalRef}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          zIndex: 9999,
          background: NW.white,
          borderRadius: 20,
          boxShadow:
            "0 24px 80px rgba(24,75,140,0.22),0 4px 20px rgba(15,23,42,0.12)",
          width: "min(560px,96vw)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: FONTS.body,
          animation: "nwSlideUp 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div
          style={{
            background:
              mode === "edit"
                ? `linear-gradient(135deg,${NW.violetDark} 0%,#1D5BA8 55%,#1D5BA8 100%)`
                : `linear-gradient(135deg,${NW.violetDark} 0%,${NW.violet} 55%,#1D5BA8 100%)`,
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            position: "relative",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background: `linear-gradient(90deg,transparent,${NW.rose}AA,${NW.rose}DD,${NW.rose}AA,transparent)`,
            }}
          />
          {/* Icône */}
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: "rgba(255,255,255,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              flexShrink: 0,
              boxShadow: "0 0 0 1px rgba(255,255,255,0.18)",
            }}
          >
            {mode === "edit" ? "✏️" : "👁️"}
          </div>
          {/* Titres */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 800,
                fontSize: 15,
                color: "#FFFFFF",
                letterSpacing: -0.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {mode === "edit" ? "Modifier la requête" : q.name}
            </div>
            <div
              style={{
                fontSize: 10,
                color: "rgba(255,255,255,0.55)",
                marginTop: 2,
                letterSpacing: 1.1,
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              {mode === "edit"
                ? "Édition · Mise à jour"
                : "Détail · Requête sauvegardée"}
            </div>
          </div>
          {/* Badges type/section (mode vue) */}
          {mode === "view" && (typeMeta || sectionMeta) && (
            <div
              style={{
                display: "flex",
                gap: 5,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {typeMeta && (
                <span
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    borderRadius: 7,
                    padding: "2px 9px",
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    whiteSpace: "nowrap",
                  }}
                >
                  {typeMeta.icon} {typeMeta.label}
                </span>
              )}
              {sectionMeta && (
                <span
                  style={{
                    background: "rgba(255,255,255,0.10)",
                    border: "1px solid rgba(255,255,255,0.20)",
                    borderRadius: 7,
                    padding: "2px 9px",
                    fontSize: 10,
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.85)",
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    whiteSpace: "nowrap",
                  }}
                >
                  {sectionMeta.icon} {sectionMeta.label}
                </span>
              )}
            </div>
          )}
          {/* ✕ Fermer */}
          <button
            onClick={onClose}
            title="Fermer"
            style={{
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.20)",
              borderRadius: 8,
              width: 30,
              height: 30,
              cursor: "pointer",
              color: "rgba(255,255,255,0.80)",
              fontSize: 17,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        {/* ── Body scrollable ──────────────────────────────────────────────── */}
        <div
          style={{
            overflowY: "auto",
            flex: 1,
            padding: "18px 20px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {/* ════════ MODE VUE ════════ */}
          {mode === "view" && (
            <>
              {/* Mesures (noms réels) */}
              <Section
                title="📐 Mesures"
                color={NW.violet}
                bg={NW.violetSoft}
                border={NW.violetMed}
              >
                {measures.length === 0 ? (
                  <span
                    style={{
                      fontSize: 11,
                      color: "#64748B",
                      fontStyle: "italic",
                    }}
                  >
                    Aucune mesure
                  </span>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {measures.map((m, i) => (
                      <span
                        key={i}
                        style={{
                          background: `${NW.violet}18`,
                          color: NW.violet,
                          border: `1px solid ${NW.violet}30`,
                          borderRadius: 6,
                          padding: "3px 10px",
                          fontSize: 11,
                          fontWeight: 700,
                          fontFamily: FONTS.body,
                        }}
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                )}
              </Section>

              {/* Vue & Affichage */}
              {(typeMeta || sectionMeta) && (
                <Section
                  title="🗂️ Vue & Affichage"
                  color="#334155"
                  bg={NW.gray50}
                  border={NW.gray200}
                >
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {typeMeta && (
                      <span
                        style={{
                          background: typeMeta.bg,
                          color: typeMeta.color,
                          border: `1px solid ${typeMeta.color}30`,
                          borderRadius: 6,
                          padding: "3px 10px",
                          fontSize: 11,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {typeMeta.icon} {typeMeta.label}
                      </span>
                    )}
                    {sectionMeta && (
                      <span
                        style={{
                          background: sectionMeta.bg,
                          color: sectionMeta.color,
                          border: `1px solid ${sectionMeta.color}30`,
                          borderRadius: 6,
                          padding: "3px 10px",
                          fontSize: 11,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {sectionMeta.icon} {sectionMeta.label}
                      </span>
                    )}
                  </div>
                </Section>
              )}

              {/* Dimensions */}
              {(rowDims.length > 0 || colDims.length > 0) && (
                <Section
                  title="↕↔ Dimensions"
                  color="#1D5BA8"
                  bg="#1D5BA808"
                  border="#1D5BA825"
                >
                  {rowDims.length > 0 && (
                    <DimRow
                      label="Lignes"
                      color="#184B8C"
                      items={rowDims.map(getDimLabel)}
                    />
                  )}
                  {colDims.length > 0 && (
                    <DimRow
                      label="Colonnes"
                      color="#4A86C7"
                      items={colDims.map(getDimLabel)}
                    />
                  )}
                </Section>
              )}

              {/* Période (hors comparaison) */}
              {!isComparison && (q.dateFrom || soldeCode || demarquePhase) && (
                <Section
                  title="📅 Période"
                  color={NW.teal}
                  bg="#00A08C08"
                  border="#00A08C25"
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {soldeCode && (
                      <span
                        style={{
                          background: "#D3EFEA",
                          color: "#006357",
                          border: "1px solid #A9DFD7",
                          borderRadius: 6,
                          padding: "3px 10px",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        🏷️ {soldeCode}
                      </span>
                    )}
                    {demarquePhase && (
                      <span
                        style={{
                          background: NW.violetLight,
                          color: NW.violetDark,
                          border: `1px solid ${NW.violetMed}`,
                          borderRadius: 6,
                          padding: "3px 10px",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        📉 {demarquePhase}
                      </span>
                    )}
                    {q.dateFrom && (
                      <span
                        style={{
                          background: "#00A08C14",
                          color: NW.teal,
                          border: "1px solid #00A08C30",
                          borderRadius: 6,
                          padding: "3px 10px",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {q.dateFrom}
                        {q.dateTo ? ` → ${q.dateTo}` : ""}
                      </span>
                    )}
                  </div>
                </Section>
              )}

              {/* Périodes de comparaison */}
              {isComparison && compPeriods && compPeriods.length > 0 && (
                <Section
                  title="⚖️ Périodes comparées"
                  color={NW.blue}
                  bg="#1D5BA808"
                  border="#1D5BA825"
                >
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    {compPeriods.map((p, pi) => {
                      const colors = [
                        "#184B8C",
                        "#7DA8D9",
                        "#00A08C",
                        "#D98E04",
                        "#1D5BA8",
                      ];
                      const c = colors[pi % 5];
                      return (
                        <div
                          key={pi}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            flexWrap: "wrap",
                            background: `${c}08`,
                            borderRadius: 7,
                            padding: "5px 8px",
                            border: `1px solid ${c}20`,
                          }}
                        >
                          <span
                            style={{
                              background: c,
                              color: "#FFFFFF",
                              borderRadius: 4,
                              padding: "1px 6px",
                              fontSize: 10,
                              fontWeight: 800,
                            }}
                          >
                            P{pi + 1}
                          </span>
                          {p.label && (
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: NW.gray800,
                                background: NW.white,
                                border: `1px solid ${NW.gray200}`,
                                borderRadius: 4,
                                padding: "1px 6px",
                              }}
                            >
                              {p.label}
                            </span>
                          )}
                          <span
                            style={{
                              background: "#1D5BA814",
                              color: NW.blue,
                              border: "1px solid #1D5BA830",
                              borderRadius: 4,
                              padding: "1px 7px",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {p.dateFrom} → {p.dateTo}
                          </span>
                          {p.soldeCodePeriode && (
                            <span
                              style={{
                                background: "#D3EFEA",
                                color: "#006357",
                                border: "1px solid #A9DFD7",
                                borderRadius: 4,
                                padding: "1px 6px",
                                fontSize: 10,
                                fontWeight: 700,
                              }}
                            >
                              🏷️ {p.soldeCodePeriode}
                            </span>
                          )}
                          {p.demarquePhase && (
                            <span
                              style={{
                                background: NW.violetLight,
                                color: NW.violetDark,
                                border: `1px solid ${NW.violetMed}`,
                                borderRadius: 4,
                                padding: "1px 6px",
                                fontSize: 10,
                                fontWeight: 700,
                              }}
                            >
                              📉 {p.demarquePhase}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Section>
              )}

              {/* Remarque */}
              {q.remarque && (
                <Section
                  title="📝 Remarque"
                  color="#334155"
                  bg={NW.gray50}
                  border={NW.gray200}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: 12,
                      color: NW.gray700,
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {q.remarque}
                  </p>
                </Section>
              )}

              {/* Date création */}
              <div
                style={{ fontSize: 10, color: NW.gray300, textAlign: "right" }}
              >
                Créée le {fmtDate(q.createdAt)}
              </div>
            </>
          )}

          {/* ════════ MODE ÉDITION ════════ */}
          {mode === "edit" && (
            <>
              {/* Résumé */}
              <div
                style={{
                  background: NW.violetSoft,
                  border: `1.5px solid ${NW.violetMed}`,
                  borderRadius: 10,
                  padding: "10px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <div
                  style={{
                    fontFamily: FONTS.heading,
                    fontWeight: 700,
                    fontSize: 10,
                    color: NW.violet,
                    textTransform: "uppercase",
                    letterSpacing: 1.2,
                  }}
                >
                  ◈ Requête sélectionnée
                </div>
                <div
                  style={{
                    fontFamily: FONTS.heading,
                    fontWeight: 700,
                    fontSize: 13,
                    color: NW.gray800,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {q.name}
                </div>
              </div>

              {/* Champ Nom */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label
                  style={{
                    fontFamily: FONTS.heading,
                    fontWeight: 700,
                    fontSize: 12,
                    color: NW.gray700,
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  🏷️ Nom de la requête
                  <span
                    style={{ fontSize: 10, color: NW.danger, fontWeight: 700 }}
                  >
                    *
                  </span>
                </label>
                <input
                  autoFocus
                  type="text"
                  value={editName}
                  onChange={(e) => {
                    setEditName(e.target.value);
                    setSaveError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                  }}
                  maxLength={120}
                  placeholder="Nom de la requête…"
                  style={{
                    border: `1.5px solid ${saveError && !editName.trim() ? NW.danger : editName ? NW.violet : NW.gray300}`,
                    borderRadius: 10,
                    padding: "10px 14px",
                    fontSize: 13,
                    fontFamily: FONTS.body,
                    color: NW.gray800,
                    outline: "none",
                    background: NW.white,
                    transition: "border-color 0.15s, box-shadow 0.15s",
                    boxShadow: editName
                      ? `0 0 0 3px ${NW.violetMed}44`
                      : "none",
                  }}
                />
                <div
                  style={{
                    fontSize: 10,
                    color: NW.gray500,
                    textAlign: "right",
                  }}
                >
                  {editName.length} / 120
                </div>
              </div>

              {/* Champ Remarque (effaçable) */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label
                  style={{
                    fontFamily: FONTS.heading,
                    fontWeight: 700,
                    fontSize: 12,
                    color: NW.gray700,
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  📝 Remarque
                  <span
                    style={{
                      fontSize: 10,
                      color: NW.gray500,
                      fontWeight: 400,
                      fontFamily: FONTS.body,
                    }}
                  >
                    (optionnelle)
                  </span>
                </label>
                <div style={{ position: "relative" }}>
                  <textarea
                    value={editRemarque}
                    onChange={(e) => setEditRemarque(e.target.value)}
                    placeholder="Ajoutez une note, un contexte ou une description…"
                    maxLength={500}
                    rows={3}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      border: `1.5px solid ${editRemarque ? NW.violet : NW.gray300}`,
                      borderRadius: 10,
                      padding: "10px 40px 10px 14px",
                      fontSize: 12,
                      fontFamily: FONTS.body,
                      color: NW.gray800,
                      outline: "none",
                      background: NW.white,
                      resize: "vertical",
                      minHeight: 72,
                      transition: "border-color 0.15s",
                      boxShadow: editRemarque
                        ? `0 0 0 3px ${NW.violetMed}44`
                        : "none",
                      lineHeight: 1.6,
                    }}
                  />
                  {/* Bouton effacer */}
                  {editRemarque && (
                    <button
                      onClick={() => setEditRemarque("")}
                      title="Effacer la remarque"
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        background: NW.dangerLight,
                        border: `1px solid ${NW.danger}44`,
                        borderRadius: 6,
                        width: 22,
                        height: 22,
                        cursor: "pointer",
                        color: NW.danger,
                        fontSize: 11,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: NW.gray500,
                    textAlign: "right",
                  }}
                >
                  {editRemarque.length} / 500
                </div>
              </div>

              {/* Erreur */}
              {saveError && (
                <div
                  style={{
                    background: NW.dangerLight,
                    border: `1.5px solid ${NW.danger}44`,
                    borderRadius: 10,
                    padding: "10px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 12,
                    color: NW.danger,
                    fontWeight: 600,
                  }}
                >
                  <span style={{ fontSize: 15 }}>⚠️</span>
                  {saveError}
                </div>
              )}

              {/* Succès */}
              {saveSuccess && (
                <div
                  style={{
                    background: NW.successLight,
                    border: `1.5px solid ${NW.success}44`,
                    borderRadius: 10,
                    padding: "10px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 12,
                    color: NW.success,
                    fontWeight: 700,
                  }}
                >
                  <span style={{ fontSize: 15 }}>✅</span>
                  Requête modifiée avec succès !
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer boutons ───────────────────────────────────────────────── */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: `1px solid ${NW.gray100}`,
            background: NW.gray50,
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            flexShrink: 0,
          }}
        >
          {mode === "view" ? (
            <>
              <button onClick={onClose} style={btnSecondary}>
                ✕ Fermer
              </button>
              <button onClick={() => setMode("edit")} style={btnPrimaryBase}>
                ✏️ Modifier
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  setMode("view");
                  setSaveError(null);
                  setSaveSuccess(false);
                }}
                disabled={saving}
                style={btnSecondary}
              >
                ← Retour
              </button>
              <button
                onClick={handleSave}
                disabled={saving || saveSuccess}
                style={{
                  ...btnPrimaryBase,
                  background:
                    saving || saveSuccess
                      ? NW.gray200
                      : `linear-gradient(135deg,${NW.violetDark} 0%,${NW.violet} 55%,#1D5BA8 100%)`,
                  color: saving || saveSuccess ? NW.gray500 : "#FFFFFF",
                  cursor: saving || saveSuccess ? "not-allowed" : "pointer",
                  boxShadow:
                    saving || saveSuccess
                      ? "none"
                      : "0 4px 14px rgba(24,75,140,0.30)",
                }}
              >
                {saving ? (
                  <>
                    <span
                      style={{
                        width: 13,
                        height: 13,
                        borderRadius: "50%",
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderTopColor: "#FFFFFF",
                        display: "inline-block",
                        animation: "nwSpin 0.7s linear infinite",
                      }}
                    />
                    Enregistrement…
                  </>
                ) : saveSuccess ? (
                  "✅ Enregistré !"
                ) : (
                  <>💾 Enregistrer</>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes nwFadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes nwSlideUp {
          from{opacity:0;transform:translate(-50%,calc(-50% + 16px)) scale(0.96)}
          to  {opacity:1;transform:translate(-50%,-50%) scale(1)}
        }
        @keyframes nwSpin { to{transform:rotate(360deg)} }
      `}</style>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ── SavedQueriesDropdown (composant principal) ────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
const SavedQueriesDropdown: React.FC<Props> = ({
  onSelect,
  refreshToken,
  currentType,
}) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [queries, setQueries] = useState<SavedQuery[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<string | null>(
    currentType ?? null,
  );

  // ── requête affichée dans la modal détail ─────────────────────────────────
  const [detailQuery, setDetailQuery] = useState<SavedQuery | null>(null);

  useEffect(() => {
    setFilterType(currentType ?? null);
  }, [currentType]);

  // ── Chargement ────────────────────────────────────────────────────────────
  const fetchQueries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getSavedQueries();
      setQueries(data);
    } catch (e: any) {
      setError(e.message ?? "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchQueries();
  }, [open, refreshToken, fetchQueries]);

  // Fermer dropdown sur clic extérieur (modal reste gérée séparément)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (detailQuery) return;
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !dropRef.current?.contains(t)) {
        setOpen(false);
        setDeleteConfirm(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [detailQuery]);

  // ── Suppression ───────────────────────────────────────────────────────────
  const handleDelete = async (id: number) => {
    try {
      await deleteSavedQuery(id);
      setQueries((prev) => prev.filter((q) => q.id !== id));
      setDeleteConfirm(null);
    } catch (e: any) {
      setError(e.message ?? "Erreur suppression");
    }
  };

  // ── Mise à jour immédiate (dropdown reste ouvert) ─────────────────────────
  const handleUpdated = (updated: SavedQuery) => {
    setQueries((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
    setDetailQuery(updated); // rafraîchir la modal aussi
  };

  // ── Filtrage ──────────────────────────────────────────────────────────────
  // Si currentType est défini (ex: "vendeur"), on affiche UNIQUEMENT
  // les requêtes dont le type correspond exactement à la page courante.
  // Sans currentType → tout afficher (comportement page MDX ou générique).
  const baseQueries = queries.filter((q) => {
    if (!currentType) return true;
    return (q as any).type === currentType;
  });

  const filtered = baseQueries.filter((q) => {
    const matchText = q.name.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === null || q.type === filterType;
    return matchText && matchType;
  });

  const availableTypes = Array.from(
    new Set(baseQueries.map((q) => q.type).filter(Boolean)),
  ) as string[];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {/* Modal détail/édition */}
      {detailQuery && (
        <DetailModal
          query={detailQuery}
          onClose={() => setDetailQuery(null)}
          onUpdated={handleUpdated}
        />
      )}

      {/* Bouton déclencheur */}
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        title="Requêtes sauvegardées"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 14px",
          borderRadius: 8,
          border: `1.5px solid ${open ? NW.violet : NW.gray200}`,
          background: open ? NW.violetSoft : NW.white,
          color: open ? NW.violet : NW.gray700,
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          fontFamily: FONTS.heading,
          transition: "all 0.15s",
          boxShadow: open ? `0 0 0 3px ${NW.violetMed}66` : "none",
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ fontSize: 14 }}>📂</span>
        Requêtes sauvegardées
        {baseQueries.length > 0 && (
          <span
            style={{
              background: NW.violet,
              color: "#FFFFFF",
              borderRadius: 99,
              fontSize: 10,
              padding: "1px 6px",
              fontWeight: 700,
              lineHeight: 1.6,
            }}
          >
            {filtered.length}
          </span>
        )}
        <span
          style={{
            fontSize: 10,
            marginLeft: 1,
            display: "inline-block",
            transition: "transform 0.15s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          ▼
        </span>
      </button>

      {/* Panneau dropdown */}
      {open && (
        <div
          ref={dropRef}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 9990,
            background: NW.white,
            borderRadius: 14,
            boxShadow:
              "0 12px 48px rgba(24,75,140,0.18),0 2px 8px rgba(15,23,42,0.10)",
            border: `1.5px solid ${NW.violetMed}`,
            width: 420,
            overflow: "hidden",
            fontFamily: FONTS.body,
            animation: "nwDropDown 0.18s cubic-bezier(0.34,1.2,0.64,1)",
          }}
        >
          {/* En-tête */}
          <div
            style={{
              background: `linear-gradient(135deg,${NW.violetDark} 0%,${NW.violet} 100%)`,
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background: `linear-gradient(90deg,transparent,${NW.rose}BB,transparent)`,
              }}
            />
            <span style={{ fontSize: 16 }}>📂</span>
            <div
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 800,
                fontSize: 13,
                color: "#FFFFFF",
                flex: 1,
              }}
            >
              Requêtes sauvegardées
            </div>
            <span
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.5)",
                fontWeight: 600,
              }}
            >
              {filtered.length}
            </span>
          </div>

          {/* Recherche + filtres */}
          <div
            style={{
              padding: "10px 12px 6px",
              borderBottom: `1px solid ${NW.gray100}`,
              background: NW.gray50,
              display: "flex",
              flexDirection: "column",
              gap: 7,
            }}
          >
            <input
              autoFocus
              type="text"
              placeholder="🔍 Rechercher une requête…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                border: `1.5px solid ${search ? NW.violet : NW.gray200}`,
                borderRadius: 8,
                padding: "6px 12px",
                fontSize: 12,
                fontFamily: FONTS.body,
                outline: "none",
                background: NW.white,
                color: NW.gray800,
                boxSizing: "border-box",
                transition: "border-color 0.15s",
              }}
            />
            {availableTypes.length > 1 && (
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                <button
                  onClick={() => setFilterType(null)}
                  style={{
                    fontSize: 10,
                    padding: "2px 9px",
                    borderRadius: 99,
                    border: `1.5px solid ${filterType === null ? NW.violet : NW.gray200}`,
                    background: filterType === null ? NW.violet : NW.white,
                    color: filterType === null ? "#FFFFFF" : NW.gray500,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: FONTS.body,
                  }}
                >
                  Tous
                </button>
                {availableTypes.map((t) => {
                  const meta = TYPE_META[t];
                  const active = filterType === t;
                  return (
                    <button
                      key={t}
                      onClick={() => setFilterType(active ? null : t)}
                      style={{
                        fontSize: 10,
                        padding: "2px 9px",
                        borderRadius: 99,
                        border: `1.5px solid ${active ? (meta?.color ?? NW.violet) : NW.gray200}`,
                        background: active
                          ? (meta?.color ?? NW.violet)
                          : NW.white,
                        color: active ? "#FFFFFF" : (meta?.color ?? NW.gray500),
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: FONTS.body,
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      {meta?.icon ?? "◈"} {meta?.label ?? t}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Liste */}
          <div style={{ maxHeight: 280, overflowY: "auto" }}>
            {loading && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "16px",
                  color: NW.gray500,
                  fontSize: 12,
                }}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: `2px solid ${NW.violetMed}`,
                    borderTopColor: NW.violet,
                    display: "inline-block",
                    animation: "nwSpin 0.7s linear infinite",
                    flexShrink: 0,
                  }}
                />
                Chargement…
              </div>
            )}
            {error && (
              <div
                style={{
                  padding: "10px 14px",
                  color: NW.danger,
                  fontSize: 12,
                  background: NW.dangerLight,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                ⚠ {error}
              </div>
            )}
            {!loading && !error && filtered.length === 0 && (
              <div
                style={{
                  padding: "28px 16px",
                  textAlign: "center",
                  color: NW.gray500,
                  fontSize: 12,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 28 }}>📭</span>
                {search || filterType
                  ? "Aucune requête ne correspond au filtre."
                  : "Aucune requête sauvegardée."}
              </div>
            )}

            {!loading &&
              !error &&
              filtered.map((q) => {
                const typeMeta = (q as any).type
                  ? TYPE_META[(q as any).type]
                  : undefined;
                const sectionMeta = (q as any).section
                  ? SECTION_META[(q as any).section]
                  : undefined;
                const rowDims: any[] = q.rowsDimensions ?? [];
                const colDims: any[] = (q as any).columnsDimensions ?? [];
                const soldeCode: string | null =
                  (q as any).soldeCodePeriode ?? null;
                const compPeriods = parseComparisonPeriods(
                  (q as any).comparisonPeriods,
                );
                const isComparison =
                  (q as any).section === "comparaison" ||
                  (q as any).section === "comparison";
                const measures: string[] = (q as any).measures ?? [];

                return (
                  <div
                    key={q.id}
                    style={{
                      padding: "10px 14px",
                      borderBottom: `1px solid ${NW.gray100}`,
                      background: NW.white,
                      cursor: onSelect ? "pointer" : "default",
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLElement).style.background =
                        NW.violetSoft)
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLElement).style.background =
                        NW.white)
                    }
                    onClick={() => {
                      if (onSelect) {
                        onSelect(q);
                        setOpen(false);
                      }
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      {/* Infos */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Nom */}
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: NW.gray800,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontFamily: FONTS.heading,
                          }}
                        >
                          {q.name}
                        </div>

                        {/* Badges */}
                        {(typeMeta || sectionMeta) && (
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              marginTop: 4,
                              flexWrap: "wrap",
                            }}
                          >
                            {typeMeta && (
                              <span
                                style={{
                                  background: typeMeta.bg,
                                  color: typeMeta.color,
                                  border: `1px solid ${typeMeta.color}30`,
                                  borderRadius: 5,
                                  padding: "1px 7px",
                                  fontSize: 10,
                                  fontWeight: 700,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 3,
                                }}
                              >
                                {typeMeta.icon} {typeMeta.label}
                              </span>
                            )}
                            {sectionMeta && (
                              <span
                                style={{
                                  background: sectionMeta.bg,
                                  color: sectionMeta.color,
                                  border: `1px solid ${sectionMeta.color}30`,
                                  borderRadius: 5,
                                  padding: "1px 7px",
                                  fontSize: 10,
                                  fontWeight: 700,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 3,
                                }}
                              >
                                {sectionMeta.icon} {sectionMeta.label}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Métadonnées compactes — mesures réelles */}
                        <div
                          style={{
                            fontSize: 10,
                            color: NW.gray500,
                            marginTop: 5,
                            display: "flex",
                            gap: 5,
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          {measures.length > 0 && (
                            <span
                              title={measures.join(", ")}
                              style={{
                                background: `${NW.violet}18`,
                                color: NW.violet,
                                borderRadius: 4,
                                padding: "1px 6px",
                                fontWeight: 700,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 3,
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              📐 {measures.join(", ")}
                            </span>
                          )}
                          {rowDims.length > 0 && (
                            <span
                              title={rowDims.map(getDimLabel).join(", ")}
                              style={{
                                background: "#184B8C14",
                                color: "#184B8C",
                                borderRadius: 4,
                                padding: "1px 6px",
                                fontWeight: 700,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 3,
                                border: "1px solid #184B8C30",
                              }}
                            >
                              ↕ {rowDims.map(getDimLabel).join(", ")}
                            </span>
                          )}
                          {colDims.length > 0 && (
                            <span
                              title={colDims.map(getDimLabel).join(", ")}
                              style={{
                                background: "#7DA8D914",
                                color: "#4A86C7",
                                borderRadius: 4,
                                padding: "1px 6px",
                                fontWeight: 700,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 3,
                                border: "1px solid #7DA8D930",
                              }}
                            >
                              ↔ {colDims.map(getDimLabel).join(", ")}
                            </span>
                          )}
                          {q.dateFrom && !soldeCode && !isComparison && (
                            <span
                              style={{
                                background: "#00A08C14",
                                color: "#00A08C",
                                borderRadius: 4,
                                padding: "1px 6px",
                                fontWeight: 700,
                              }}
                            >
                              📅 {q.dateFrom}
                              {q.dateTo ? ` → ${q.dateTo}` : ""}
                            </span>
                          )}
                          {soldeCode && (
                            <span
                              style={{
                                background: "#D3EFEA",
                                color: "#006357",
                                border: "1px solid #A9DFD7",
                                borderRadius: 4,
                                padding: "1px 7px",
                                fontWeight: 700,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 3,
                              }}
                            >
                              🏷️ {soldeCode}
                              {q.dateFrom && q.dateTo && (
                                <span
                                  style={{
                                    opacity: 0.7,
                                    fontWeight: 500,
                                    marginLeft: 2,
                                  }}
                                >
                                  ({q.dateFrom} → {q.dateTo})
                                </span>
                              )}
                            </span>
                          )}
                          {isComparison && compPeriods && (
                            <span
                              style={{
                                background: "#1D5BA814",
                                color: "#1D5BA8",
                                border: "1px solid #1D5BA830",
                                borderRadius: 4,
                                padding: "1px 7px",
                                fontWeight: 700,
                              }}
                            >
                              ⚖️ {compPeriods.length} période
                              {compPeriods.length > 1 ? "s" : ""}
                            </span>
                          )}
                          <span
                            style={{
                              color: NW.gray500,
                              marginLeft: "auto",
                              fontSize: 10,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {fmtDate(q.createdAt)}
                          </span>
                        </div>
                      </div>

                      {/* Boutons action */}
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: "flex",
                          gap: 4,
                          flexShrink: 0,
                          alignItems: "center",
                        }}
                      >
                        {deleteConfirm === q.id ? (
                          <div style={{ display: "flex", gap: 4 }}>
                            <button
                              onClick={() => handleDelete(q.id)}
                              style={{
                                fontSize: 10,
                                padding: "2px 7px",
                                borderRadius: 5,
                                border: `1px solid ${NW.danger}`,
                                background: NW.dangerLight,
                                color: NW.danger,
                                cursor: "pointer",
                                fontWeight: 700,
                                fontFamily: FONTS.body,
                              }}
                            >
                              Oui, supprimer
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              style={{
                                fontSize: 10,
                                padding: "2px 7px",
                                borderRadius: 5,
                                border: `1px solid ${NW.gray200}`,
                                background: NW.white,
                                color: NW.gray500,
                                cursor: "pointer",
                                fontFamily: FONTS.body,
                              }}
                            >
                              Non
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* 👁️ Voir détail */}
                            <button
                              onClick={() => setDetailQuery(q)}
                              title="Voir le détail"
                              style={iconBtnBase}
                              onMouseEnter={(e) => {
                                (
                                  e.currentTarget as HTMLElement
                                ).style.background = NW.violetLight;
                                (
                                  e.currentTarget as HTMLElement
                                ).style.borderColor = NW.violet;
                                (e.currentTarget as HTMLElement).style.color =
                                  NW.violet;
                              }}
                              onMouseLeave={(e) => {
                                (
                                  e.currentTarget as HTMLElement
                                ).style.background = "none";
                                (
                                  e.currentTarget as HTMLElement
                                ).style.borderColor = NW.gray200;
                                (e.currentTarget as HTMLElement).style.color =
                                  NW.gray500;
                              }}
                            >
                              👁️
                            </button>

                            {/* 🗑 Supprimer */}
                            <button
                              onClick={() => setDeleteConfirm(q.id)}
                              title="Supprimer"
                              style={iconBtnBase}
                              onMouseEnter={(e) => {
                                (
                                  e.currentTarget as HTMLElement
                                ).style.background = NW.dangerLight;
                                (
                                  e.currentTarget as HTMLElement
                                ).style.borderColor = NW.danger;
                                (e.currentTarget as HTMLElement).style.color =
                                  NW.danger;
                              }}
                              onMouseLeave={(e) => {
                                (
                                  e.currentTarget as HTMLElement
                                ).style.background = "none";
                                (
                                  e.currentTarget as HTMLElement
                                ).style.borderColor = NW.gray200;
                                (e.currentTarget as HTMLElement).style.color =
                                  NW.gray500;
                              }}
                            >
                              🗑
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Pied */}
          <div
            style={{
              padding: "8px 14px",
              borderTop: `1px solid ${NW.gray100}`,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={fetchQueries}
              disabled={loading}
              style={{
                fontSize: 11,
                color: NW.violet,
                fontWeight: 700,
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: FONTS.body,
                display: "flex",
                alignItems: "center",
                gap: 4,
                opacity: loading ? 0.5 : 1,
              }}
            >
              🔄 Actualiser
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes nwDropDown {
          from{opacity:0;transform:translateY(-8px) scale(0.97)}
          to  {opacity:1;transform:translateY(0) scale(1)}
        }
        @keyframes nwSpin { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
};

export default SavedQueriesDropdown;
